/*
  Geometrie der Flugbahn — reine Funktionen, kein DOM-Zustand, keine Messungen.
  Die Messungen macht BeautyAirwaysRoot.vue und reicht fertige Punkte hier
  herein.

  EINE Bahn: splineThrough() legt Anflug und Reise als eine Kurve durch alle
  Stützpunkte, in Container-Koordinaten (px, relativ zum Routen-Container).
  Streng monoton in y, damit die Position eindeutig aus der Scroll-Front folgt
  (lengthAtY). Sie scrollt mit dem Inhalt.

  Der Abflug braucht deshalb keine eigene Kurve mehr: das Logo ist einfach der
  erste Stützpunkt, und `head` gibt die senkrechte Anfangstangente vor (= Nase
  unten, exakt die Haltung, mit der der Morph endet). Es gibt keine Naht, an
  der Tangente oder Krümmung springen könnten.

  Frühere Fassungen hatten hier zwei Konstruktionen für den Startbogen
  (takeoffThrough/archThrough) — sie sind mit dem einen Pfad entfallen. Der
  Stand mit beiden liegt unter beauty-airways/archiv.
*/

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v)
const f2 = (n) => n.toFixed(2)

/**
 * y streng aufsteigend erzwingen. Layout-Messungen können gleiche oder (bei
 * überlappenden Blöcken) fallende y liefern — dann wäre lengthAtY() nicht
 * eindeutig und das Flugzeug würde springen.
 */
export function monotonizeY(pts, minGap = 24) {
    const out = []
    for (const p of pts) {
        const prev = out[out.length - 1]
        out.push(prev && p.y <= prev.y + minGap ? { x: p.x, y: prev.y + minGap } : { x: p.x, y: p.y })
    }
    return out
}

/**
 * Waagerechte Auslenkung je Abschnitt begrenzen.
 *
 * Die Wegpunkte kommen aus dem Layout: bei schmalem Viewport oder engen
 * Blockabständen liegen zwei Motive weit auseinander in x und nah in y — die
 * Bahn wird dann zur Zickzack-Linie. `maxSlope` (dx/dy) deckelt das: die Bahn
 * darf pro vertikalem Pixel höchstens `maxSlope` Pixel seitwärts. Damit bleibt
 * die Kurve gleich ruhig, egal wie viele Blöcke da sind oder wie breit es ist.
 *
 * Läuft von oben nach unten und zieht jeden Punkt nur so weit zurück, wie er
 * das Limit verletzt — die gepflegte Seite (links/rechts) bleibt also erhalten,
 * nur der Ausschlag wird kleiner.
 */
export function limitSlope(pts, maxSlope = 0.8) {
    if (pts.length < 2) return pts
    const out = [{ ...pts[0] }]
    for (let i = 1; i < pts.length; i++) {
        const prev = out[i - 1]
        const budget = Math.max(0, pts[i].y - prev.y) * maxSlope
        const dx = pts[i].x - prev.x
        out.push({
            x: Math.abs(dx) <= budget ? pts[i].x : prev.x + Math.sign(dx) * budget,
            y: pts[i].y,
        })
    }
    return out
}

/**
 * Catmull-Rom-Spline durch `pts` als Pfad-`d`.
 *
 * Die y-Werte der Kontrollpunkte werden in [p1.y, p2.y] geklemmt. Eine Kubik
 * mit y0 <= y1 <= y2 <= y3 ist in y monoton — damit ist garantiert, dass die
 * Bahn nie zurück nach oben schwingt, egal wie weit die Punkte seitlich
 * auseinanderliegen. Ohne diese Klemme kann Catmull-Rom bei engen y- und
 * weiten x-Abständen überschwingen und lengthAtY() mehrdeutig machen.
 */
export function splineThrough(pts, tension = 0.9, head = null, mid = null) {
    if (!pts || pts.length < 2) return ''
    let d = `M ${f2(pts[0].x)} ${f2(pts[0].y)}`
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i]
        const p1 = pts[i]
        const p2 = pts[i + 1]
        const p3 = pts[i + 2] || pts[i + 1]
        const c1 = { x: p1.x + ((p2.x - p0.x) / 6) * tension, y: p1.y + ((p2.y - p0.y) / 6) * tension }
        /*
          PROTOTYP — Griff am ERSTEN Punkt frei setzen: `head = { len, dir }`.

          Ohne Vorgänger fällt (p2 - p0) auf (p2 - p1) zusammen, der Griff ist
          also nur ein Sechstel der Strecke zum nächsten Knoten — mal tension
          rund 15 %, und seine Richtung zeigt zwangsläufig auf den nächsten
          Punkt.

          Beides muss sich vorgeben lassen, wenn die Kurve am Logo beginnt:
          `dir` erzwingt den senkrechten Abflug (die Kontur endet mit der Nase
          nach unten, die Übergabe vom Morph wäre sonst ein Umklappen), `len`
          bestimmt, wie lange sie senkrecht bleibt.
        */
        if (i === 0 && head && head.len > 0) {
            const hx = head.dir ? head.dir.x : p2.x - p1.x
            const hy = head.dir ? head.dir.y : p2.y - p1.y
            const len = Math.hypot(hx, hy) || 1
            c1.x = p1.x + (hx / len) * head.len
            c1.y = p1.y + (hy / len) * head.len
        }
        const c2 = { x: p2.x - ((p3.x - p1.x) / 6) * tension, y: p2.y - ((p3.y - p1.y) / 6) * tension }
        /*
          PROTOTYP — Tangente am ZWEITEN Punkt frei setzen: `mid = { dir, len }`.

          Catmull-Rom bildet sie aus den beiden Nachbarn (p2 - p0), hier also
          aus Logo und erstem Knoten. Die Lage des Punktes selbst geht NICHT
          ein — deshalb ändert ein weiterer Bogen oben nichts an der Form
          darunter, gemessen bleibt die Pfeilhöhe des unteren Stücks bei 19 bis
          27 px, egal wie weit der Bogen läuft.

          Mit einer eigenen Tangente entscheidet dieser Punkt, ob die Kurve ihn
          noch nach außen laufend passiert oder schon zurückschwenkt: nach
          links geneigt liegt der Scheitel DAVOR, und das Stück darunter baucht
          sich zur anderen Seite.

          Die RICHTUNG gilt für beide Seiten — sonst entstünde hier ein Knick.
          Die LÄNGEN sind getrennt (`lenIn` / `lenOut`), und das ist wesentlich:
          die beiden Abschnitte sind verschieden lang und wollen Verschiedenes.
          Oben soll die Kurve weich ankommen (langer Griff), unten früh
          einlenken statt gerade durchzulaufen (kurzer Griff). Mit einer
          gemeinsamen Länge erkauft man das eine mit dem anderen — gemessen
          sprang die Krümmung dabei kurz vor dem Punkt auf das Dreifache.
        */
        if (mid && i === 0) {
            const l = Math.hypot(mid.dir.x, mid.dir.y) || 1
            c2.x = p2.x - (mid.dir.x / l) * mid.lenIn
            c2.y = p2.y - (mid.dir.y / l) * mid.lenIn
        }
        if (mid && i === 1) {
            const l = Math.hypot(mid.dir.x, mid.dir.y) || 1
            c1.x = p1.x + (mid.dir.x / l) * mid.lenOut
            c1.y = p1.y + (mid.dir.y / l) * mid.lenOut
        }
        c1.y = clamp(c1.y, p1.y, p2.y)
        c2.y = clamp(c2.y, c1.y, p2.y)
        d += ` C ${f2(c1.x)} ${f2(c1.y)} ${f2(c2.x)} ${f2(c2.y)} ${f2(p2.x)} ${f2(p2.y)}`
    }
    return d
}

/**
 * Tabelle Bogenlänge <-> y für einen in y monotonen Pfad. Wird einmal pro
 * Layout gebaut (steps+1 getPointAtLength-Aufrufe), pro Frame kostet die
 * Abfrage dann nur eine Binärsuche.
 *
 * Die Dichte hängt an der LÄNGE des Pfades, nicht an einer festen Zahl: eine
 * Stützstelle je 20 px. Der Aufbau ist mit Abstand der teuerste Teil eines
 * Neuaufbaus, und er läuft bei jeder Fensteränderung erneut. Gemessen auf
 * einem 3597 px langen Pfad:
 *
 *   360 Stufen -> 19.5 ms
 *   180 Stufen ->  8.7 ms, größter Fehler von lengthAtY 0.19 px
 *   120 Stufen ->  5.8 ms, größter Fehler 0.41 px
 *
 * Zwischen den Stützstellen wird linear interpoliert, und y über die Länge
 * verläuft auf diesen kurzen Abschnitten praktisch gerade — der Fehler bleibt
 * deshalb weit unter einem Pixel. Die feste Vorgabe von 360 war reine
 * Vorsicht: sie kostete das Doppelte für eine Genauigkeit, die niemand sieht.
 */
export function sampleTable(pathEl, steps) {
    const total = pathEl.getTotalLength()
    if (!steps) steps = Math.round(clamp(total / 20, 90, 240))
    const ys = new Float64Array(steps + 1)
    const ls = new Float64Array(steps + 1)
    for (let i = 0; i <= steps; i++) {
        const l = (i / steps) * total
        ls[i] = l
        ys[i] = pathEl.getPointAtLength(l).y
    }
    // Rundungsrauschen der Sampling-Punkte glattbügeln, damit die Binärsuche
    // unten auf einer wirklich monotonen Folge arbeitet.
    for (let i = 1; i <= steps; i++) if (ys[i] < ys[i - 1]) ys[i] = ys[i - 1]
    return { ys, ls, total }
}

/** Bogenlänge an der Höhe `y` (gleiche Koordinaten wie der Pfad). */
export function lengthAtY(table, y) {
    const { ys, ls, total } = table
    const n = ys.length - 1
    if (y <= ys[0]) return 0
    if (y >= ys[n]) return total
    let lo = 0
    let hi = n
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1
        if (ys[mid] <= y) lo = mid
        else hi = mid
    }
    const span = ys[hi] - ys[lo]
    return ls[lo] + (ls[hi] - ls[lo]) * (span > 1e-6 ? (y - ys[lo]) / span : 0)
}

/**
 * Punkt + Kurswinkel an Bogenlänge `len`. Winkel in Grad, 0 = Nase oben —
 * das ist die Konvention von planeTransform().
 */
export function pointAt(pathEl, len, total) {
    const l = clamp(len, 0, total)
    const a = pathEl.getPointAtLength(l)
    // Am Pfadende gibt es kein "danach" -> Tangente aus dem Punkt davor.
    const back = l + 1.5 > total
    const b = pathEl.getPointAtLength(back ? Math.max(0, l - 1.5) : l + 1.5)
    const dx = back ? a.x - b.x : b.x - a.x
    const dy = back ? a.y - b.y : b.y - a.y
    return { x: a.x, y: a.y, angle: (Math.atan2(dy, dx) * 180) / Math.PI + 90 }
}

export { clamp }
